import { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, MessageCircle, BookOpen } from "lucide-react";
import { PageShell } from "../components/PageShell";
import { MaskLines, Reveal } from "../components/Reveal";
import { useLang, IMG, WHATSAPP, ADDRESS, MAPS } from "../lib/i18n";

const C = {
  it: {
    locale: "it-IT",
    ref: (n) => `Sei stata invitata da ${n}: una sorpresa ti aspetta al tuo primo trattamento.`,
    over: "Richiesta ricevuta",
    title: ["Grazie", "ci vediamo presto."],
    hi: (n) => (n ? `${n}, la tua richiesta è arrivata a Gioia.` : "La tua richiesta è arrivata a Gioia."),
    sub: "Ti ricontatteremo a breve per confermare giorno e orario. Ecco come si svolgerà la tua prima valutazione.",
    recap: "Riepilogo", service: "Trattamento", date: "Data preferita", time: "Orario",
    stepsTitle: "Come si svolge la prima valutazione",
    steps: [
      ["Conferma dell'appuntamento", "Ti contattiamo telefonicamente o su WhatsApp per fissare il giorno e l'orario più comodi per te."],
      ["Accoglienza e ascolto", "Ti accogliamo in un ambiente pulito e riservato. Parliamo delle tue abitudini, della routine che segui, dei tuoi obiettivi e di eventuali allergie o sensibilità."],
      ["Analisi professionale", "Gioia valuta con attenzione la tua pelle o la zona del corpo da trattare: idratazione, tono, elasticità e caratteristiche specifiche."],
      ["Il tuo percorso su misura", "In base a quanto emerso ti proponiamo il trattamento più adatto e la frequenza consigliata, spiegandoti con chiarezza cosa aspettarti."],
      ["Consigli per casa", "Ti suggeriamo i gesti e i prodotti giusti, anche della linea Gioia Skincare, per mantenere e prolungare i risultati."],
    ],
    prepTitle: "Come prepararti",
    prep: [
      "Per i trattamenti viso, se puoi, arriva senza trucco.",
      "Porta con te i nomi dei prodotti che usi abitualmente.",
      "Segnalaci farmaci, allergie o condizioni particolari della pelle.",
      "Arriva qualche minuto prima per goderti il momento senza fretta.",
    ],
    whereTitle: "Dove ci trovi", directions: "Indicazioni",
    need: "Hai bisogno di modificare la richiesta?", wa: "Scrivici su WhatsApp", back: "Torna al sito", blog: "Leggi il blog",
  },
  en: {
    locale: "en-GB",
    ref: (n) => `You were invited by ${n}: a surprise awaits you at your first treatment.`,
    over: "Request received",
    title: ["Thank you", "see you soon."],
    hi: (n) => (n ? `${n}, your request has reached Gioia.` : "Your request has reached Gioia."),
    sub: "We'll be in touch shortly to confirm the day and time. Here is how your first assessment will go.",
    recap: "Summary", service: "Treatment", date: "Preferred date", time: "Time",
    stepsTitle: "How your first assessment works",
    steps: [
      ["Appointment confirmation", "We contact you by phone or WhatsApp to set the day and time that suit you best."],
      ["Welcome and listening", "We welcome you in a clean, private setting. We talk about your habits, current routine, goals and any allergies or sensitivities."],
      ["Professional analysis", "Gioia carefully assesses your skin or the body area to be treated: hydration, tone, elasticity and specific features."],
      ["Your tailored programme", "Based on the assessment, we suggest the most suitable treatment and recommended frequency, explaining clearly what to expect."],
      ["Home-care advice", "We recommend the right steps and products, including the Gioia Skincare line, to maintain and extend your results."],
    ],
    prepTitle: "How to prepare",
    prep: [
      "For facial treatments, come without make-up if you can.",
      "Bring the names of the products you use regularly.",
      "Let us know about medication, allergies or particular skin conditions.",
      "Arrive a few minutes early to enjoy the moment without rushing.",
    ],
    whereTitle: "Where to find us", directions: "Directions",
    need: "Need to change your request?", wa: "Message us on WhatsApp", back: "Back to site", blog: "Read the blog",
  },
};

const Recap = ({ c, s }) => {
  const rows = [[c.service, s.service], [c.date, s.date ? new Date(s.date).toLocaleDateString(c.locale, { day: "numeric", month: "long", year: "numeric" }) : null], [c.time, s.time]].filter(([, v]) => v);
  if (!rows.length) return null;
  return (
    <div className="mt-10 border-t border-[#ddd4c7]" data-testid="thankyou-recap">
      <p className="overline text-[0.62rem] text-[#8A6A3A] pt-6">{c.recap}</p>
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between gap-6 py-3 border-b border-[#ddd4c7] text-sm">
          <span className="text-[#5a534c]">{k}</span><span className="text-[#14110F] text-right">{v}</span>
        </div>
      ))}
    </div>
  );
};

export default function ThankYou() {
  const { lang } = useLang();
  const c = C[lang];
  const s = useLocation().state ?? {};
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <PageShell>
      <div className="max-w-[1440px] mx-auto" data-testid="thankyou-page">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <div className="lg:col-span-6">
            <motion.div initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 160, damping: 14, delay: 0.1 }}
              className="w-16 h-16 rounded-full bg-[#14110F] text-[#C9A66B] flex items-center justify-center">
              <Check className="w-7 h-7" strokeWidth={1.5} />
            </motion.div>
            <p className="mt-10 overline text-[#8A6A3A]">{c.over}</p>
            <h1 className="mt-5 font-serif-d font-light text-[3rem] sm:text-7xl leading-[0.95] text-[#14110F]" data-testid="thankyou-title">
              <MaskLines lines={[c.title[0]]} delay={0.2} />
              <MaskLines lines={[c.title[1]]} delay={0.35} lineClass="italic text-[#8A6A3A]" />
            </h1>
            <Reveal delay={0.3}>
              <p className="mt-8 font-serif-d text-2xl text-[#14110F]" data-testid="thankyou-greeting">{c.hi(s.name)}</p>
              <p className="mt-3 text-[#4f4841] font-light leading-relaxed max-w-lg">{c.sub}</p>
              {s.referrer && <p className="mt-4 text-sm text-[#8A6A3A]" data-testid="thankyou-referrer">{c.ref(s.referrer)}</p>}
              <Recap c={c} s={s} />
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-6">
            <div className="relative overflow-hidden aspect-[4/5] sm:aspect-[5/4] lg:aspect-[4/5] rounded-t-[220px]">
              <img src={IMG.cabina} alt="Gioia Anfosso in cabina" className="absolute inset-0 w-full h-full object-cover object-[70%_25%]" />
            </div>
          </Reveal>
        </div>

        <section className="mt-24 sm:mt-32" data-testid="thankyou-steps">
          <Reveal><h2 className="font-serif-d font-light text-4xl sm:text-6xl text-[#14110F] max-w-3xl">{c.stepsTitle}</h2></Reveal>
          <ol className="mt-14 grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {c.steps.map(([h, d], i) => (
              <motion.li key={h} data-testid={`thankyou-step-${i}`}
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
                transition={{ duration: 0.9, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className={`p-7 flex flex-col gap-10 ${i === 2 ? "bg-[#14110F] text-[#F6F2EC]" : "bg-[#ECE5DA] text-[#14110F]"}`}>
                <span className={`font-stencil tracking-[0.2em] ${i === 2 ? "text-[#C9A66B]" : "text-[#8A6A3A]"}`}>0{i + 1}</span>
                <div>
                  <h3 className="font-serif-d text-2xl leading-tight">{h}</h3>
                  <p className={`mt-3 text-sm leading-relaxed font-light ${i === 2 ? "text-[#d9d0c4]" : "text-[#4f4841]"}`}>{d}</p>
                </div>
              </motion.li>
            ))}
          </ol>
        </section>

        <section className="mt-20 grid lg:grid-cols-12 gap-4">
          <Reveal className="lg:col-span-7">
            <div className="h-full border border-[#ddd4c7] p-8 sm:p-10" data-testid="thankyou-prep">
              <h2 className="font-serif-d text-3xl sm:text-4xl text-[#14110F]">{c.prepTitle}</h2>
              <ul className="mt-8 space-y-4">
                {c.prep.map((p) => (
                  <li key={p} className="flex gap-4 text-[#4f4841] font-light"><Check className="w-4 h-4 mt-1 shrink-0 text-[#8A6A3A]" />{p}</li>
                ))}
              </ul>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="h-full bg-[#14110F] text-[#F6F2EC] p-8 sm:p-10 grain relative overflow-hidden flex flex-col justify-between gap-10">
              <div>
                <h2 className="font-serif-d text-3xl">{c.whereTitle}</h2>
                <p className="mt-3 text-[#d9d0c4]">{ADDRESS}</p>
                <a href={MAPS} target="_blank" rel="noreferrer" data-testid="thankyou-directions-link" className="mt-4 inline-block text-sm underline underline-offset-4 decoration-[#C9A66B] hover:text-[#C9A66B]">{c.directions}</a>
              </div>
              <div>
                <p className="text-sm text-[#b8ad9f]">{c.need}</p>
                <a href={WHATSAPP} target="_blank" rel="noreferrer" data-testid="thankyou-whatsapp-btn" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#C9A66B] text-[#14110F] px-6 py-3 text-[0.72rem] tracking-[0.2em] uppercase transition-colors duration-500 hover:bg-[#F6F2EC]">
                  <MessageCircle className="w-4 h-4" /> {c.wa}
                </a>
              </div>
            </div>
          </Reveal>
        </section>

        <div className="mt-16 flex flex-col sm:flex-row gap-4">
          <Link to="/" data-testid="thankyou-back-btn" className="inline-flex justify-center rounded-full bg-[#14110F] text-[#F6F2EC] px-8 py-3.5 text-[0.72rem] tracking-[0.2em] uppercase transition-colors duration-500 hover:bg-[#8A6A3A]">{c.back}</Link>
          <Link to="/blog" data-testid="thankyou-blog-btn" className="inline-flex justify-center items-center gap-2 rounded-full border border-[#14110F] px-8 py-3.5 text-[0.72rem] tracking-[0.2em] uppercase text-[#14110F] transition-colors duration-500 hover:bg-[#14110F] hover:text-[#F6F2EC]">
            <BookOpen className="w-4 h-4" /> {c.blog}
          </Link>
        </div>
      </div>
    </PageShell>
  );
}
